import React from "react";
import { MultiSelect } from "@progress/kendo-react-dropdowns";

// Stored value is a comma-separated list of plan IDs. This helper turns it back
// into an array of trimmed, non-empty IDs.
export const splitPlanIds = (value) =>
  typeof value === "string"
    ? value
        .split(",")
        .map((token) => token.trim())
        .filter((token) => token.length > 0)
    : [];

/**
 * Multi-select for "Plans available for contribution" (HSA/FSA) and the reused
 * DCA "Plans available for contribution" field.
 *
 * - `options` are the selectable plans as `{ value, label }`.
 * - `value` is the stored comma-separated list of plan IDs.
 * - `onChange` receives a synthetic event `{ target: { name, value } }` so the
 *   host form's existing change handler works unchanged.
 *
 * Any stored ID that is no longer one of the current model plans is preserved
 * and shown flagged so a legacy selection is never silently dropped.
 */
const PlanContributionMultiSelect = ({
  id,
  name,
  options = [],
  value = "",
  onChange,
  disabled = false,
  placeholder = "Select plans...",
  style,
}) => {
  const selectedIds = splitPlanIds(value);

  const data = React.useMemo(() => {
    const known = options.map((option) => ({
      text: option.label,
      value: option.value,
      missing: false,
    }));
    const knownValues = new Set(known.map((option) => option.value));
    // Recompute selected ids inside the memo so the deps array is complete.
    const currentSelectedIds = splitPlanIds(value);
    const missing = currentSelectedIds
      .filter((selectedId) => !knownValues.has(selectedId))
      .map((selectedId) => ({
        text: `${selectedId} (not in current model)`,
        value: selectedId,
        missing: true,
      }));

    return [...known, ...missing];
  }, [options, value]);

  const selected = data.filter((option) => selectedIds.includes(option.value));

  const handleChange = (event) => {
    const nextValues = (event.value ?? []).map((item) => item.value);
    onChange?.({ target: { name, value: nextValues.join(", ") } });
  };

  return (
    <MultiSelect
      id={id}
      name={name}
      data={data}
      textField="text"
      dataItemKey="value"
      value={selected}
      onChange={handleChange}
      disabled={disabled}
      placeholder={placeholder}
      style={style}
    />
  );
};

export default PlanContributionMultiSelect;
